import React, { ReactNode, RefObject, useEffect, useRef } from 'react';

import ListBox from '../Listbox/ListBox';

import { IEntity } from '../../../../../../../types/api/entities/IEntity';
import { List } from '../List/List';
import { Selected } from '../hooks/usePopupKeyboardNavigation';
import { Section } from '../types/Section';

export interface PreviewResultPopUpProps<T extends IEntity> {
  id: string;
  sections: Section<T>[];
  allResultsWithoutLinks?: T[];
  selectedItem?: T;
  formInputRef: RefObject<HTMLInputElement | null>;
  hidden: boolean;
  loading?: boolean;
  modifier?: string;
  error?: string;
  onSelection: (event: Selected<T>) => void;
  children: (item?: T) => ReactNode;
  onBlur: () => void;
}

export default function PreviewResultPopUp<T extends IEntity>(props: PreviewResultPopUpProps<T>) {
  const selectedItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedHtmlElementItemId =
      !props.hidden && props.selectedItem ? `combobox-option-${props.id}-${props.selectedItem.id}` : undefined;

    if (selectedHtmlElementItemId) {
      /* Need to do this to allow formComboBoxes input field to expose correct
       information to screen readers. */
      props.formInputRef.current?.setAttribute('aria-activedescendant', selectedHtmlElementItemId);
    } else {
      props.formInputRef.current?.removeAttribute('aria-activedescendant');
    }
  }, [props.formInputRef, props.hidden, props.id, props.selectedItem]);

  const getResults = (section: Section<T>) => {
    if (props.allResultsWithoutLinks) {
      if (props.allResultsWithoutLinks.length > 0) {
        return section.results;
      }
      return [];
    }
    return section.results;
  };

  const renderSection = (section: Section<T>) => {
    return (
      <List
        key={section.key}
        results={getResults(section)}
        id={props.id}
        clickHandler={props.onSelection}
        selectedItemId={props.selectedItem?.id}
        selectedItemRef={selectedItemRef}
        headline={section.headline}
      >
        {props.children}
      </List>
    );
  };
  return (
    <ListBox
      id={props.id}
      modifier={props.modifier}
      ariaHidden={props.hidden}
      selectedItemRef={selectedItemRef}
      loading={!!props.loading}
      error={props.error}
      onBlur={props.onBlur}
    >
      {!props.hidden ? props.sections.map(renderSection) : undefined}
    </ListBox>
  );
}
